import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Snowflake, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-sky-500 border-2 border-slate-900 shadow-[4px_4px_0px_0px_#0f172a] flex items-center justify-center">
            <Snowflake className="w-7 h-7 text-white" />
          </div>
          <div className="font-display text-2xl uppercase leading-none text-slate-900">
            On <span className="bg-sky-500 text-white px-1.5 -skew-x-3 inline-block">Ice</span>
          </div>
        </div>

        <div className="card-brutal shadow-[8px_8px_0px_0px_#0f172a] p-6 md:p-8">
          <h1 className="font-display text-2xl uppercase text-slate-900 mb-1">New password</h1>
          <p className="text-sm text-slate-600 mb-6">Choose a new password (min 8 characters).</p>

          {!token ? (
            <div className="border-2 border-rose-500 bg-rose-50 text-rose-700 p-3 font-mono-tight text-xs font-bold" data-testid="reset-password-missing-token">
              This reset link is missing its token. Please request a new one.
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              {error && (
                <div className="border-2 border-rose-500 bg-rose-50 text-rose-700 p-3 font-mono-tight text-xs font-bold mb-4" data-testid="reset-password-error">
                  {error}
                </div>
              )}
              <label className="label-brutal" htmlFor="reset-password">New password</label>
              <input
                id="reset-password"
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                className="input-brutal mb-4"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label className="label-brutal" htmlFor="reset-confirm">Confirm password</label>
              <input
                id="reset-confirm"
                data-testid="reset-password-confirm-input"
                type="password"
                required
                minLength={8}
                className="input-brutal mb-6"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="reset-password-submit-button">
                <KeyRound className="w-4 h-4" /> {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link to="/login" className="font-mono-tight text-xs uppercase tracking-widest font-bold text-sky-600 hover:text-sky-800 underline" data-testid="reset-back-to-login-link">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
